import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowRight, BookOpen, FileText, LayoutGrid, MapPin, Plus, Search, User, X } from "lucide-react";
import client, { unwrap } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { formatINR, timeAgo } from "@/lib/format";
import { PROJECTS_HERO_IMAGE, roomImageForSeed } from "@/lib/stockImages";
import PageHero from "@/components/layout/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const CLOSED_PROJECT_STATUSES = ["COMPLETED", "CANCELLED", "CLOSED"];
const OPEN_QUOTATION_STATUSES = ["DRAFT", "SUBMITTED_FOR_APPROVAL", "CHANGES_REQUIRED"];

// Stage is derived from the project's rooms and its latest quotation, since
// project.status is only ever ACTIVE or a closed state today.
const STAGES = [
  { key: "NEW", label: "New", chip: "bg-card/95 text-foreground", dot: "bg-chart-3" },
  { key: "IN_DESIGN", label: "In Design", chip: "bg-[hsl(41_70%_90%)] text-[hsl(41_60%_28%)]", dot: "bg-warning" },
  { key: "QUOTATION", label: "Quotation", chip: "bg-[hsl(214_60%_92%)] text-[hsl(214_45%_35%)]", dot: "bg-[hsl(214_55%_50%)]" },
  { key: "FINALIZED", label: "Finalized", chip: "bg-primary text-primary-foreground", dot: "bg-[hsl(152_50%_70%)]" },
  { key: "CLOSED", label: "Closed", chip: "bg-secondary text-muted-foreground", dot: "bg-muted-foreground" },
];
const STAGE_BY_KEY = Object.fromEntries(STAGES.map((s) => [s.key, s]));

const SORTS = {
  updated: { label: "Last updated", fn: (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt) },
  name: { label: "Name (A–Z)", fn: (a, b) => a.name.localeCompare(b.name) },
  value: { label: "Quotation value", fn: (a, b) => (b.quotationValue || 0) - (a.quotationValue || 0) },
};

function stageFor(project, latestQuotation) {
  if (CLOSED_PROJECT_STATUSES.includes(project.status)) return "CLOSED";
  if (latestQuotation && !OPEN_QUOTATION_STATUSES.includes(latestQuotation.status)) return "FINALIZED";
  if (latestQuotation) return "QUOTATION";
  if (project.roomCount > 0) return "IN_DESIGN";
  return "NEW";
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-start gap-2 min-w-0">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.75} />
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground leading-tight truncate">{value}</p>
        <p className="text-[11px] text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function ProjectCard({ p }) {
  const stage = STAGE_BY_KEY[p.stage];
  const location = [p.city, p.address].filter(Boolean)[0];
  return (
    <Link
      to={`/projects/${p._id}`}
      data-testid={`project-card-${p._id}`}
      className="group bg-card border border-border rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all hi-animate-fade-up"
    >
      <div className="relative h-36 overflow-hidden">
        <img
          src={roomImageForSeed(p._id)}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
        />
        <span className={cn("absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium shadow-sm", stage.chip)}>
          <span className={cn("h-1.5 w-1.5 rounded-full", stage.dot)} />
          {stage.label}
        </span>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading text-lg text-foreground leading-snug truncate">{p.name}</h3>
          <span className="h-8 w-8 rounded-full bg-accent/10 text-accent flex items-center justify-center shrink-0 group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
        <div className="mt-1.5 space-y-1 text-sm text-muted-foreground">
          <p className="flex items-center gap-2 truncate">
            <User className="h-3.5 w-3.5 shrink-0" /> {p.customerName}
          </p>
          <p className="flex items-center gap-2 min-w-0">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{location || "No location yet"}</span>
            {p.bhk && <span className="ml-1 shrink-0 rounded-md bg-secondary px-2 py-0.5 text-[11px] text-foreground/80">{p.bhk}</span>}
          </p>
        </div>

        <div className="mt-4 pt-3 border-t border-border grid grid-cols-3 gap-2">
          <Stat icon={BookOpen} value={p.roomCount ?? "—"} label={p.roomCount === 1 ? "Room" : "Rooms"} />
          <Stat icon={LayoutGrid} value={p.windowCount ?? "—"} label={p.windowCount === 1 ? "Window" : "Windows"} />
          <Stat icon={FileText} value={p.quotationValue ? formatINR(p.quotationValue) : "—"} label="Quotation" />
        </div>

        <p className="mt-3 text-xs text-muted-foreground text-right">Updated {timeAgo(p.updatedAt)}</p>
      </div>
    </Link>
  );
}

function NewProjectDialog({ open, setOpen, customers, defaultCustomerId }) {
  const queryClient = useQueryClient();
  const empty = { customerId: defaultCustomerId, name: "", address: "", bhk: "", city: "" };
  const [form, setForm] = useState(empty);

  const createMutation = useMutation({
    mutationFn: (payload) => client.post("/projects", payload),
    onSuccess: () => {
      toast.success("Project created");
      setOpen(false);
      setForm(empty);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="add-project-dialog">
        <DialogHeader><DialogTitle className="font-heading">New Project</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Customer</Label>
            <Select value={form.customerId} onValueChange={(v) => setForm({ ...form, customerId: v })}>
              <SelectTrigger data-testid="project-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Project Name</Label><Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Address</Label><Input data-testid="project-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>City</Label><Input data-testid="project-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>BHK</Label><Input data-testid="project-bhk-input" placeholder="e.g. 3 BHK" value={form.bhk} onChange={(e) => setForm({ ...form, bhk: e.target.value })} /></div>
          </div>
          <Button
            data-testid="project-submit-button"
            className="w-full"
            disabled={!form.customerId || !form.name || createMutation.isPending}
            onClick={() => createMutation.mutate(form)}
          >
            Create
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectsPage() {
  const [params, setParams] = useSearchParams();
  const customerIdFilter = params.get("customerId") || "";
  const { hasPermission } = useAuth();
  const canSeeQuotations = hasPermission("quotation:create");
  const [open, setOpen] = useState(false);
  const [stageTab, setStageTab] = useState("ALL");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("updated");

  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: () => client.get("/customers").then(unwrap) });
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects", customerIdFilter],
    queryFn: () => client.get(`/projects${customerIdFilter ? `?customerId=${customerIdFilter}` : ""}`).then(unwrap),
  });
  const { data: quotations = [] } = useQuery({
    queryKey: ["quotations-all"],
    queryFn: () => client.get("/quotations").then(unwrap),
    enabled: canSeeQuotations,
  });

  const cards = useMemo(() => {
    const customerName = Object.fromEntries(customers.map((c) => [c._id, c.name]));
    const latestQuotation = {};
    for (const q of quotations) {
      const cur = latestQuotation[q.projectId];
      if (!cur || new Date(q.updatedAt) > new Date(cur.updatedAt)) latestQuotation[q.projectId] = q;
    }
    return projects.map((p) => {
      const q = latestQuotation[p._id];
      return {
        ...p,
        customerName: customerName[p.customerId] || "Customer",
        quotationValue: q?.grandTotal || 0,
        stage: stageFor(p, q),
      };
    });
  }, [projects, customers, quotations]);

  const stageCounts = useMemo(() => {
    const counts = Object.fromEntries(STAGES.map((s) => [s.key, 0]));
    cards.forEach((c) => (counts[c.stage] += 1));
    return counts;
  }, [cards]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return cards
      .filter((c) => stageTab === "ALL" || c.stage === stageTab)
      .filter((c) => !term || [c.name, c.customerName, c.city, c.address].some((v) => v?.toLowerCase().includes(term)))
      .sort(SORTS[sort].fn);
  }, [cards, stageTab, search, sort]);

  const filteredCustomer = customers.find((c) => c._id === customerIdFilter);
  const tabs = [{ key: "ALL", label: "All Projects", count: cards.length }, ...STAGES.map((s) => ({ ...s, count: stageCounts[s.key] }))];

  return (
    <div data-testid="projects-page" className="space-y-6">
      <PageHero
        kpiOverlap={false}
        image={PROJECTS_HERO_IMAGE}
        tagline="From ideas to beautifully furnished spaces."
        title="Projects"
        subtitle="Rooms, windows and quotations live inside each project."
        action={
          <Button data-testid="add-project-button" className="rounded-full px-5" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> New Project
          </Button>
        }
      />

      <div className="flex flex-col 2xl:flex-row 2xl:items-center gap-3">
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" role="tablist">
          {tabs.map((t) => {
            const active = stageTab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                data-testid={`project-stage-tab-${t.key}`}
                onClick={() => setStageTab(t.key)}
                className={cn(
                  "shrink-0 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors",
                  active ? "bg-primary border-primary text-primary-foreground" : "bg-card border-border text-foreground hover:bg-secondary",
                )}
              >
                {t.label}
                <span className={cn("rounded-full px-1.5 min-w-[1.25rem] text-xs tabular-nums", active ? "bg-primary-foreground/20" : "text-muted-foreground")}>
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 2xl:ml-auto 2xl:w-auto">
          <div className="relative flex-1 sm:min-w-[320px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="projects-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by project, customer, location…"
              className="w-full rounded-full border border-border bg-card pl-10 pr-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="projects-sort" className="sm:w-[190px] rounded-full bg-card h-auto py-2.5">
              <span className="text-muted-foreground mr-1">Sort:</span> <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SORTS).map(([key, s]) => <SelectItem key={key} value={key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {customerIdFilter && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Showing projects for</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary pl-3 pr-1.5 py-1 text-foreground" data-testid="projects-customer-filter">
            {filteredCustomer?.name || "this customer"}
            <button
              aria-label="Show all customers"
              onClick={() => setParams({})}
              className="h-5 w-5 rounded-full hover:bg-border flex items-center justify-center"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {visible.map((p) => <ProjectCard key={p._id} p={p} />)}
      </div>

      {!isLoading && visible.length === 0 && (
        <div className="bg-card border border-dashed border-border rounded-2xl py-14 text-center">
          <p className="text-sm text-muted-foreground">
            {projects.length === 0 ? "No projects yet. Create one to start adding rooms and windows." : "No projects match this view."}
          </p>
          {projects.length > 0 && (
            <button
              onClick={() => {
                setStageTab("ALL");
                setSearch("");
              }}
              className="mt-2 text-sm font-medium text-accent hover:underline"
            >
              Show all projects
            </button>
          )}
        </div>
      )}

      <NewProjectDialog open={open} setOpen={setOpen} customers={customers} defaultCustomerId={customerIdFilter} />
    </div>
  );
}
